import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { AddReportIssue } from "../../API/ReportIssue/AddReportIssue";
import ReactGA from "react-ga4";
import { useLocalization } from "../Context/MultiLanguageContext";

const MAX_REPORT_LENGTH = 100;

const ReportIssue: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [reportContent, setReportContent] = useState('');
  const [subject, setSubject] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userName, setUserName] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);

  useEffect(() => {
    setIsFormChanged(reportContent !== '' || subject !== '');
  }, [reportContent, subject]);

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) setUserName(storedUserName);
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate('/profile', { state: data });
    } catch {
      toast.error(t("sessionExpired"));
      navigate('/home');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate('/home');
      return;
    }

    // ✅ Validation
    if (!subject.trim()) {
      toast.error(t("subjectRequired") || "Subject is required");
      return;
    }

    if (!reportContent.trim()) {
      toast.error(t("reportRequired") || "Report content cannot be empty");
      return;
    }

    if (reportContent.length > MAX_REPORT_LENGTH) {
      toast.error(`Maximum ${MAX_REPORT_LENGTH} characters allowed`);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AddReportIssue(reportContent, subject, token);

      if (response) {
        ReactGA.event({
          category: "Report Issue",
          action: "Submitted Report Issue",
          label: subject,
        });

        toast.success(t("reportSuccess") || "Report submitted successfully");

        setReportContent('');
        setSubject('');
        setIsFormChanged(false);
      }
    } catch (error: any) {
      toast.error(error.message || t("feedbackError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormChanged && !isSubmitting) {
      e.preventDefault();
      handleReportSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 overflow-hidden">
      <Header
        userName={userName}
        fetchUserProfile={fetchAndHandleUserProfile}
        handleLogout={handleLogout}
      />

      <div className="flex-1 p-4 md:p-6">
        <h1 className="text-xl font-bold mb-6 text-center">
          {t("reportIssue")}
        </h1>

        <div className="w-full mx-auto p-4 md:p-8 bg-white rounded-lg">
          <form className="space-y-4" onSubmit={handleReportSubmit}>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("subject")}
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("feedbackSubject")}
className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"              />
            </div>

            {/* Report Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("reportContent")}
              </label>
              <textarea
                value={reportContent}
                onChange={(e) => {
                  const value = e.target.value;

                  // ✅ Restrict to 100 chars
                  if (value.length <= MAX_REPORT_LENGTH) {
                    setReportContent(value);
                  }
                }}
                onKeyDown={handleKeyDown}
                rows={5}
className={`w-full px-4 py-2 border rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${reportContent.length >= MAX_REPORT_LENGTH ? 'border-red-500' : 'border-gray-200'}`}
                placeholder={t("feedbackContent")}
              />

              {/* ✅ Character Counter */}
              <p className={`text-right text-sm mt-1 ${
                reportContent.length >= MAX_REPORT_LENGTH ? 'text-red-500' : 'text-gray-500'
              }`}>
                {reportContent.length}/{MAX_REPORT_LENGTH}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-4 mt-4">
              <button
                type="submit"
                disabled={
                  !isFormChanged ||
                  isSubmitting ||
                  !subject.trim() ||
                  !reportContent.trim() ||
                  reportContent.length > MAX_REPORT_LENGTH
                }
                className={`py-2 px-4 text-white rounded-md
                  ${isFormChanged && !isSubmitting && reportContent.length <= MAX_REPORT_LENGTH && subject.trim() && reportContent.trim()
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'}`}
              >
                {isSubmitting ? t("submitting") : t("submit")}
              </button>

              <button
                type="button"
                onClick={() => navigate('/more')}
                className="py-2 px-4 bg-gray-300 rounded-md hover:bg-gray-400"
              >
                {t("cancel")}
              </button>
            </div>

          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ReportIssue;